'use client';

import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

type PromptEditorElement = HTMLDivElement;

function resolveCssLengthToPixels(
  rawValue: string,
  element: HTMLElement
): number | null {
  const value = rawValue.trim();
  if (!value) return null;

  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return null;

  if (value.endsWith('rem')) {
    const rootFontSize = Number.parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return Number.isFinite(rootFontSize) ? numeric * rootFontSize : null;
  }

  if (value.endsWith('em')) {
    const elementFontSize = Number.parseFloat(
      getComputedStyle(element).fontSize
    );
    return Number.isFinite(elementFontSize) ? numeric * elementFontSize : null;
  }

  if (value.endsWith('px') || /^-?\d+(?:\.\d+)?$/.test(value)) {
    return numeric;
  }

  return null;
}

function readEditorBounds(editor: PromptEditorElement) {
  const style = getComputedStyle(editor);
  const minHeight = Number.parseFloat(style.minHeight) || 0;
  const parsedMaxHeight = Number.parseFloat(style.maxHeight);
  const maxHeight = Number.isFinite(parsedMaxHeight)
    ? parsedMaxHeight
    : Number.POSITIVE_INFINITY;
  const parsedDefaultHeight = resolveCssLengthToPixels(
    style.getPropertyValue('--workspace-prompt-default-height'),
    editor
  );
  const defaultHeight = clampHeight(
    parsedDefaultHeight ?? minHeight,
    minHeight,
    maxHeight
  );
  return { minHeight, maxHeight, defaultHeight };
}

function clampHeight(value: number, minHeight: number, maxHeight: number) {
  return Math.min(maxHeight, Math.max(minHeight, value));
}

export function useWorkspacePromptComposerSize(params: {
  editorRef: RefObject<PromptEditorElement | null>;
  prompt: string;
}) {
  const manualMinHeightRef = useRef<number | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
  } | null>(null);
  const frameRef = useRef<number | null>(null);

  const applyHeight = useCallback(() => {
    const editor = params.editorRef.current;
    if (!editor) return;

    const { minHeight, maxHeight, defaultHeight } = readEditorBounds(editor);
    editor.style.height = 'auto';

    const contentHeight = clampHeight(
      editor.scrollHeight,
      minHeight,
      maxHeight
    );
    const manualMinHeight = manualMinHeightRef.current;
    const nextHeight = clampHeight(
      Math.max(contentHeight, manualMinHeight ?? defaultHeight),
      minHeight,
      maxHeight
    );

    editor.style.height = `${nextHeight}px`;
    editor.style.overflowY =
      editor.scrollHeight > nextHeight + 1 ? 'auto' : 'hidden';
    editor.dataset.scrollable =
      editor.scrollHeight > nextHeight + 1 ? 'true' : 'false';
  }, [params.editorRef]);

  const scheduleHeight = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      applyHeight();
    });
  }, [applyHeight]);

  useLayoutEffect(() => {
    scheduleHeight();
  }, [params.prompt, scheduleHeight]);

  useEffect(() => {
    const handleResize = () => {
      const editor = params.editorRef.current;
      if (!editor) return;

      const { minHeight, maxHeight } = readEditorBounds(editor);
      if (manualMinHeightRef.current !== null) {
        manualMinHeightRef.current = clampHeight(
          manualMinHeightRef.current,
          minHeight,
          maxHeight
        );
      }
      scheduleHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [params.editorRef, scheduleHeight]);

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const editor = params.editorRef.current;
      if (!editor) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      resizeStateRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: editor.getBoundingClientRect().height,
      };
    },
    [params.editorRef]
  );

  const onResizePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const editor = params.editorRef.current;
      const state = resizeStateRef.current;
      if (!editor || !state || state.pointerId !== event.pointerId) return;

      const { minHeight, maxHeight } = readEditorBounds(editor);
      const nextHeight = clampHeight(
        state.startHeight + event.clientY - state.startY,
        minHeight,
        maxHeight
      );
      manualMinHeightRef.current = nextHeight;
      editor.style.height = `${nextHeight}px`;
      editor.style.overflowY =
        editor.scrollHeight > nextHeight + 1 ? 'auto' : 'hidden';
      editor.dataset.scrollable =
        editor.scrollHeight > nextHeight + 1 ? 'true' : 'false';
    },
    [params.editorRef]
  );

  const endResize = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const state = resizeStateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;

      resizeStateRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      scheduleHeight();
    },
    [scheduleHeight]
  );

  return {
    scheduleHeight,
    resizeHandleProps: {
      onPointerDown: onResizePointerDown,
      onPointerMove: onResizePointerMove,
      onPointerUp: endResize,
      onPointerCancel: endResize,
    },
  };
}
